import { Box, Link, Paper, Typography } from '@mui/material';

import Button from '@/components/Buttons/Button';
import useUserDetails from '@/hooks/useUserDetails';
import ExportPreviewScreen from '@/pages/DataHub/Export/ExportPreview/ExportPreviewScreen';
import PowerQueryPreviewScreen from '@/pages/DataHub/Export/ExportPreview/PowerQueryPreviewScreen';
import TokenLink from '@/pages/DataHub/Export/ExportPreview/TokenLink';
import useExportPreview from '@/pages/DataHub/Export/ExportPreview/useExportPreview';

import JsonCsvPreviewScreen from './JsonCsvPreviewScreen';
import PythonPreviewScreen from './PythonPreviewScreen';

const sqlComment =
  '-- Replace the database name GENERAL_INDEX with the actual name created when onboarding.';

const ExportPreview = ({
  previewOptionId,
  exportData
}: {
  previewOptionId: string;
  exportData: (type: string) => Promise<void>;
}) => {
  const {
    exportOption,
    capturePosthogEvent,
    handleExportData,
    scrambleApiToken,
    snowflakeSqlQuery,
    exportApiRequestString,
    exportCurlRequestString,
    scrambleCurlApiKey,
    uniqueSymbols,
    handleCopyToClipBoard
  } = useExportPreview(previewOptionId, exportData);
  const { id, buttonLabel, actionHeading, bulletPoints, previewImg } = exportOption;
  const { isUserPremium, isUserExpired } = useUserDetails();
  const renderPreview = () => {
    if (!id) {
      return (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
        >
          Please select a download option
        </Box>
      );
    }
    switch (id) {
      case 'csv': {
        return (
          <ExportPreviewScreen
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            previewImg={previewImg}
          >
            <JsonCsvPreviewScreen
              isCSV
              capturePosthogEvent={capturePosthogEvent}
            />
          </ExportPreviewScreen>
        );
      }
      case 'json': {
        return (
          <ExportPreviewScreen
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            listFooter={<TokenLink />}
            previewImg={previewImg}
          >
            <JsonCsvPreviewScreen
              capturePosthogEvent={capturePosthogEvent}
              exportCurlRequestString={scrambleCurlApiKey(exportCurlRequestString)}
              handlePrimaryButtonClick={() => {
                handleCopyToClipBoard(exportCurlRequestString);
              }}
              isCSV={false}
            />
          </ExportPreviewScreen>
        );
      }
      case 'python': {
        return (
          <ExportPreviewScreen
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            previewImg={previewImg}
          >
            <PythonPreviewScreen capturePosthogEvent={capturePosthogEvent} />
          </ExportPreviewScreen>
        );
      }
      case 'power_query': {
        return (
          <ExportPreviewScreen
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            listFooter={<TokenLink />}
            previewImg={previewImg}
            showMetadata={false}
          >
            <PowerQueryPreviewScreen
              capturePosthogEvent={capturePosthogEvent}
              exportApiUrl={exportApiRequestString}
              symbols={uniqueSymbols}
            />
          </ExportPreviewScreen>
        );
      }

      case 'gx_api': {
        return (
          <ExportPreviewScreen
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            handlePrimaryButtonClick={() => {
              handleCopyToClipBoard(exportApiRequestString);
            }}
            listFooter={<TokenLink />}
            previewImg={previewImg}
            primaryButtonLabel={buttonLabel}
            secondaryButton={
              isUserPremium &&
              !isUserExpired && (
                <Button
                  component={Link}
                  href={exportApiRequestString}
                  rel='noopener noreferrer'
                  size='small'
                  sx={{ ml: 2 }}
                  target='_blank'
                  variant='contained'
                  onClick={capturePosthogEvent}
                >
                  Launch
                </Button>
              )
            }
          >
            {isUserPremium && !isUserExpired && (
              <>
                <Paper
                  sx={{
                    m: 2,
                    p: 3,
                    userSelect: 'none',
                    backgroundColor: '#1A224D',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all'
                  }}
                >
                  {scrambleApiToken(exportApiRequestString)}
                </Paper>
                <Typography sx={{ p: 2, fontSize: '12px' }}>
                  For more information on the GX API, please visit our{' '}
                  <Link
                    href='https://docs.g-x.co/#introduction'
                    rel='noopener noreferrer'
                    target='_blank'
                    underline='hover'
                  >
                    documentation site.
                  </Link>
                </Typography>
              </>
            )}
          </ExportPreviewScreen>
        );
      }
      case 'snowflake': {
        return (
          <ExportPreviewScreen
            showSettings
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            handlePrimaryButtonClick={handleExportData}
            previewImg={previewImg}
            primaryButtonLabel={buttonLabel}
            secondaryButton={
              isUserPremium &&
              !isUserExpired && (
                <Button
                  size='small'
                  sx={{ ml: 2 }}
                  variant='contained'
                  onClick={() => {
                    handleCopyToClipBoard(`${sqlComment}\n${snowflakeSqlQuery}`);
                  }}
                >
                  Copy to clipboard
                </Button>
              )
            }
          >
            {isUserPremium && !isUserExpired && (
              <Paper
                sx={{
                  m: 2,
                  p: 3,
                  userSelect: 'none',
                  backgroundColor: '#1A224D',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}
              >
                <Typography color='info.main'>{sqlComment}</Typography>
                <div
                  dangerouslySetInnerHTML={{
                    __html: snowflakeSqlQuery.replaceAll('\n', '<br/>').replaceAll(' ', '&nbsp;')
                  }}
                />
              </Paper>
            )}
          </ExportPreviewScreen>
        );
      }
      case 'excel?type=default':
        return (
          <ExportPreviewScreen
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            handlePrimaryButtonClick={handleExportData}
            previewImg={previewImg}
            primaryButtonLabel={buttonLabel}
          />
        );
      default:
        return (
          <ExportPreviewScreen
            actionHeading={actionHeading}
            bulletPoints={bulletPoints}
            handlePrimaryButtonClick={handleExportData}
            previewImg={previewImg}
            primaryButtonLabel={buttonLabel}
            showSettings={false}
          />
        );
    }
  };
  return (
    <Paper
      sx={{
        ml: 2,
        mt: 2,
        maxHeight: 1000,
        flexGrow: 1,
        width: '35%',
        minWidth: 470,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'secondary.main',
        overflow: 'hidden'
      }}
    >
      {renderPreview()}
    </Paper>
  );
};

export default ExportPreview;
